import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import { useTranslation } from '@/store/languageStore';

interface Tracker {
  id: string;
  imei: string;
  serialNumber: string | null;
  model: string;
  iccid: string | null;
  organizationId: string | null;
  organizationName: string | null;
  userName: string | null;
  lastSeenAt: string | null;
}

const emptyForm = {
  imei: '',
  serialNumber: '',
  iccid: '',
  organizationId: '',
};

function formatWhen(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

function apiError(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { error?: string } } };
  return err.response?.data?.error || fallback;
}

export default function PersonalTrackersAdmin() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Tracker[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Tracker[] }>('/api/admin/personal-trackers');
      setRows(response.data.data ?? []);
    } catch (error) {
      toast.error(apiError(error, t('personalTrackers.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = (row: Tracker) => {
    setEditingId(row.id);
    setForm({
      imei: row.imei,
      serialNumber: row.serialNumber ?? '',
      iccid: row.iccid ?? '',
      organizationId: row.organizationId ?? '',
    });
  };

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        imei: form.imei.trim(),
        serialNumber: form.serialNumber.trim() || null,
        iccid: form.iccid.trim() || null,
        organizationId: form.organizationId || null,
      };
      if (editingId) {
        await api.put(`/api/admin/personal-trackers/${editingId}`, body);
      } else {
        await api.post('/api/admin/personal-trackers', body);
      }
      toast.success(t('personalTrackers.saved'));
      reset();
      await load();
    } catch (error) {
      toast.error(apiError(error, t('personalTrackers.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: Tracker) => {
    if (!window.confirm(t('personalTrackers.confirmDelete'))) return;
    try {
      await api.delete(`/api/admin/personal-trackers/${row.id}`);
      toast.success(t('personalTrackers.deleted'));
      if (editingId === row.id) reset();
      await load();
    } catch (error) {
      toast.error(apiError(error, t('personalTrackers.saveError')));
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-semibold text-textDefault">{t('personalTrackers.title')}</h1>
        <p className="mt-1 text-sm text-textSecondary">{t('personalTrackers.description')}</p>
      </div>

      <form
        className="grid gap-3 rounded-lg border border-border bg-bgElevated p-4 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="block text-sm">
          <span className="text-textSecondary">{t('personalTrackers.imei')}</span>
          <input
            className="mt-1 w-full rounded border border-border bg-bgContainer px-3 py-2"
            value={form.imei}
            disabled={editingId != null}
            onChange={(event) => setForm((prev) => ({ ...prev, imei: event.target.value }))}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-textSecondary">{t('personalTrackers.serial')}</span>
          <input
            className="mt-1 w-full rounded border border-border bg-bgContainer px-3 py-2"
            value={form.serialNumber}
            onChange={(event) => setForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
          />
        </label>
        <label className="block text-sm">
          <span className="text-textSecondary">{t('personalTrackers.iccid')}</span>
          <input
            className="mt-1 w-full rounded border border-border bg-bgContainer px-3 py-2"
            value={form.iccid}
            onChange={(event) => setForm((prev) => ({ ...prev, iccid: event.target.value }))}
          />
          <span className="mt-1 block text-xs text-textSecondary">{t('personalTrackers.iccidHint')}</span>
        </label>
        <label className="block text-sm">
          <span className="text-textSecondary">{t('personalTrackers.organization')}</span>
          <div className="mt-1">
            <OrgTreeSelect
              value={form.organizationId}
              onChange={(value) => setForm((prev) => ({ ...prev, organizationId: value }))}
              showEmptyOption
              emptyLabel={t('personalTrackers.noOrganization')}
            />
          </div>
        </label>
        <div className="flex gap-2 md:col-span-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-bgPrimary px-4 py-2 text-sm text-white disabled:opacity-60"
          >
            {editingId ? t('personalTrackers.save') : t('personalTrackers.add')}
          </button>
          {editingId && (
            <button type="button" className="rounded border border-border px-4 py-2 text-sm" onClick={reset}>
              {t('personalTrackers.cancel')}
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-textSecondary">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-textSecondary">{t('personalTrackers.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-bgElevated text-left text-textSecondary">
              <tr>
                <th className="px-3 py-2">{t('personalTrackers.imei')}</th>
                <th className="px-3 py-2">{t('personalTrackers.serial')}</th>
                <th className="px-3 py-2">{t('personalTrackers.iccid')}</th>
                <th className="px-3 py-2">{t('personalTrackers.organization')}</th>
                <th className="px-3 py-2">{t('personalTrackers.user')}</th>
                <th className="px-3 py-2">{t('personalTrackers.lastSeen')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{row.imei}</td>
                  <td className="px-3 py-2">{row.serialNumber || '–'}</td>
                  <td className="px-3 py-2 font-mono">{row.iccid || '–'}</td>
                  <td className="px-3 py-2">{row.organizationName || '–'}</td>
                  <td className="px-3 py-2">{row.userName || '–'}</td>
                  <td className="px-3 py-2">{formatWhen(row.lastSeenAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" className="mr-3 text-bgPrimary" onClick={() => startEdit(row)}>
                      {t('common.edit')}
                    </button>
                    <button type="button" className="text-red-600" onClick={() => void remove(row)}>
                      {t('personalTrackers.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
